import { formatRequiredIndicator } from 'client/components/common/Required/Required'
import { RequiredInterface } from 'client/components/common/Required/RequiredInterface'
import * as React from 'react'
import { ResizeObserver } from 'resize-observer'

import './AreaInput.scss'
import { InputState } from './Input/Input'
import { InputInterface } from './Input/InputInterface'

export interface AreaInputPropsInterface extends InputInterface, RequiredInterface {
  cols?: number
  rows?: number
  showCounter?: boolean
  tail?: JSX.Element
  input: HTMLTextAreaElement
  resizable?: boolean
  required?: boolean
}

export class AreaInput extends React.Component<AreaInputPropsInterface, InputState> {
  constructor(props: AreaInputPropsInterface) {
    super(props)

    const newValue = props.maxLength && props.value ? props.value.substring(0, props.maxLength) : props.value
    this.state = {
      value: newValue,
      stringValue: newValue,
      hasBlurred: true,
      height: '100%',
    }
  }

  private input: HTMLTextAreaElement
  private ro = new ResizeObserver(() => this.setState({ height: String(this.input?.clientHeight) }))

  public static defaultProps: AreaInputPropsInterface = {
    value: '',
    className: ['AreaInput'],
    automation: '',
    editable: true,
    borderless: false,
  }

  public componentDidMount() {
    if (this.input) {
      this.ro.observe(this.input)
    }
  }

  public UNSAFE_componentWillReceiveProps(props: AreaInputPropsInterface) {
    const stringValue = this.handleFormatting(props.value)
    if (this.state.hasBlurred && (this.state.value !== stringValue || this.state.stringValue !== stringValue)) {
      this.setState({
        value: stringValue,
        stringValue,
        hasBlurred: true,
      })
    }
  }

  public componentWillUnmount() {
    if (this.input) {
      this.ro.unobserve(this.input)
      this.ro.disconnect()
      this.input?.remove()
      this.input = null
    }
  }

  private handleFormatting(value: string): string {
    if (this.props.formatter) {
      return this.props.formatter.render(this.props.formatter.read(value))
    }
    return value
  }

  private handlePattern(value: string): string {
    if (!this.props.pattern) {
      return value
    }
    if (this.props.pattern.exec(value)) {
      return value
    } else {
      return this.state.value
    }
  }

  private handleOnBlur(e: Event) {
    this.setState({
      ...this.state,
      hasBlurred: true,
    })

    if (this.props.onBlur) {
      this.props.onBlur(this.state.value, this.state.stringValue)
      ;(e.target as HTMLTextAreaElement).value = this.state.value
    }
  }

  private handleOnChange(e: Event) {
    if (this.props.editable === false) {
      return
    }

    let stringValue: string = this.handlePattern((e.target as HTMLTextAreaElement).value)
    stringValue = this.props.maxLength && stringValue ? stringValue.substring(0, this.props.maxLength) : stringValue
    const value = this.handleFormatting(stringValue)

    this.setState({
      value,
      stringValue,
      hasBlurred: false,
    })

    if (this.props.onChange) {
      this.props.onChange(value, stringValue)
    }
  }

  public render(): JSX.Element {
    const classes = ['Controlled', 'Input']
    if (this.props.borderless) {
      classes.push('Borderless')
    }

    let label = null
    if (this.props.label) {
      const required = formatRequiredIndicator(this.props)
      if (this.props.maxLength && this.props.showCounter) {
        const counter = this.props.maxLength ? '(' + this.state.value.length + '/' + this.props.maxLength + ')' : ''
        label = (
          <span className="Label">
            {this.props.label}
            {required}
            <span className="counter">{counter}</span>
          </span>
        )
      } else {
        label = (
          <span
            className="Label"
            onClick={() => {
              this.input.focus()
            }}
          >
            {this.props.label}
            {required}
          </span>
        )
      }
      classes.push('hasLabel')
    }

    const baseClasses = ['Controlled', 'Input', 'AreaInput']
    if (this.props.labelPosition && this.props.labelPosition === 'top') {
      baseClasses.push('LabelOnTop')
    }
    if (this.props.invalid) {
      baseClasses.push('invalid')
    }

    let value = ''
    if (this.props.onChange) {
      value = this.props.value
    } else {
      value = this.state.stringValue
    }
    const resizableHeight = this.props?.resizable ? { height: this.state?.height } : {}

    return (
      <div style={resizableHeight} className={baseClasses.join(' ')}>
        {label}
        <textarea
          ref={(area) => {
            this.input = area
          }}
          className={classes.join(' ') + ' ' + this.props.className.join(' ')}
          data-automation={this.props.automation}
          type="text"
          disabled={this.props.disabled}
          value={value}
          onChange={this.handleOnChange.bind(this)}
          onBlur={this.handleOnBlur.bind(this)}
          onClick={this.props.onClick}
          size={this.props.size}
          maxLength={this.props.maxLength}
          rows={this.props.rows}
          cols={this.props.cols}
          placeholder={this.props.placeHolder}
          autoFocus={this.props.autoFocus}
        />
        {this.props.tail || null}
      </div>
    )
  }
}
